import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Field, Input, Combobox, Textarea, Skeleton } from "@/components/ui";
import { X, User } from "lucide-react";
import states from "states-us";
import { usePaginatedCombobox } from "@/utils";
import { PlayersServices } from "@/services/bedrock";

const fanCreationSchema = z.object({
  displayName: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  email: z.string().email("Invalid email address").or(z.literal("")).optional().nullable(),
  phoneNumber: z.string().or(z.literal("")).optional().nullable(),
  fanType: z.enum(["customer", "partner"]).default("customer"),
  defaultLanguage: z.string().optional().nullable(),
  internalIdentifier: z.string().optional().nullable(),
  stateCode: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  playerId: z.union([z.number(), z.string()]).optional().nullable(),
});

export type FanCreationSchemaType = z.infer<typeof fanCreationSchema>;

interface FanCreationFormProps {
  onSubmit: (data: FanCreationSchemaType) => void;
  isSubmitting?: boolean;
  defaultValues?: Partial<FanCreationSchemaType>;
  submitLabel?: string;
  hidePlayerSelect?: boolean;
}

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish (Español)" },
];

export function FanCreationForm({
  onSubmit,
  isSubmitting = false,
  defaultValues,
  submitLabel,
  hidePlayerSelect = false,
}: FanCreationFormProps) {
  const { control, handleSubmit, setValue } = useForm<FanCreationSchemaType>({
    resolver: zodResolver(fanCreationSchema),
    defaultValues: {
      displayName: "",
      email: "",
      phoneNumber: "",
      fanType: "customer",
      defaultLanguage: "en",
      internalIdentifier: "",
      stateCode: "",
      notes: "",
      playerId: null,
      ...defaultValues,
    },
  });

  const [selectedPlayer, setSelectedPlayer] = useState<{
    id: string | number;
    label: string;
    fullName?: string;
    email?: string;
  } | null>(null);
  const [isLoadingPlayer, setIsLoadingPlayer] = useState(false);

  useEffect(() => {
    const initPlayerId = defaultValues?.playerId;
    if (initPlayerId) {
      setIsLoadingPlayer(true);
      PlayersServices.getPlayer(initPlayerId)
        .then(player => {
          setSelectedPlayer({
            id: player.id,
            label: `${player.fullName || "No Name"} - ${player.email}`,
            fullName: player.fullName,
            email: player.email,
          });
        })
        .catch(err => console.error("Failed to load initial player details", err))
        .finally(() => setIsLoadingPlayer(false));
    } else {
      setSelectedPlayer(null);
    }
  }, [defaultValues?.playerId]);

  const getPlayers = async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(player => ({
        id: player.id,
        label: `${player.fullName || "No Name"} - ${player.email}`,
        fullName: player.fullName,
        email: player.email,
      })),
    };
  };

  const playersCombobox = usePaginatedCombobox(getPlayers, false, selectedPlayer);

  useEffect(() => {
    setValue("playerId", playersCombobox.value?.id || null);
  }, [playersCombobox.value, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {!hidePlayerSelect && (
        <Controller
          name="playerId"
          control={control}
          render={({ fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Field.Label className="text-xs font-semibold text-slate-500">Assign to Player (Optional)</Field.Label>
              {isLoadingPlayer ? (
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-slate-50 animate-pulse">
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-8 rounded-full shrink-0" />
                    <div className="flex flex-col gap-1.5">
                      <Skeleton className="h-4 w-32 rounded" />
                      <Skeleton className="h-3 w-48 rounded" />
                    </div>
                  </div>
                </div>
              ) : playersCombobox.value ? (
                <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="bg-white p-2 rounded-full border border-slate-200 shadow-sm">
                      <User className="size-4 text-slate-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-slate-700">
                        {playersCombobox.value.fullName || "No Name"}
                      </span>
                      <span className="text-xs text-slate-500">{playersCombobox.value.email}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      playersCombobox.onValueChange(null);
                    }}
                    className="p-1.5 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <Combobox {...playersCombobox} placeholder="Search for a Player..." className="bg-white" showClear />
              )}
              {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
            </Field>
          )}
        />
      )}

      <Controller
        name="displayName"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid}>
            <Field.Label className="text-xs font-semibold text-slate-500">Fan Name</Field.Label>
            <Input {...field} placeholder="e.g. John Doe" className="bg-white" />
            {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
          </Field>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Field.Label className="text-xs font-semibold text-slate-500">Email Address (Optional)</Field.Label>
              <Input {...field} value={field.value || ""} placeholder="e.g. john@example.com" className="bg-white" />
              {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Field.Label className="text-xs font-semibold text-slate-500">Phone Number (Optional)</Field.Label>
              <Input {...field} value={field.value || ""} placeholder="e.g. +1 555 123 4567" className="bg-white" />
              {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="stateCode"
          control={control}
          render={({ field }) => (
            <Field>
              <Field.Label className="text-xs font-semibold text-slate-500">State / Region (Optional)</Field.Label>
              <Combobox<string>
                value={field.value || ""}
                onValueChange={field.onChange}
                items={states.map(s => s.abbreviation.toUpperCase())}
                itemToStringLabel={code =>
                  states.find(s => s.abbreviation.toUpperCase() === code?.toUpperCase())?.name || code
                }
                placeholder="Select State..."
                className="bg-white"
                showClear
              />
            </Field>
          )}
        />

        <Controller
          name="defaultLanguage"
          control={control}
          render={({ field }) => (
            <Field>
              <Field.Label className="text-xs font-semibold text-slate-500">Preferred Language (Optional)</Field.Label>
              <Combobox<string>
                value={field.value || "en"}
                onValueChange={field.onChange}
                items={LANGUAGES.map(l => l.code)}
                itemToStringLabel={code => LANGUAGES.find(l => l.code === code)?.name || code}
                placeholder="Select Language..."
                className="bg-white"
                showClear
              />
            </Field>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="fanType"
          control={control}
          render={({ field }) => (
            <Field>
              <Field.Label className="text-xs font-semibold text-slate-500">Fan Type</Field.Label>
              <Combobox<"customer" | "partner">
                value={field.value}
                onValueChange={field.onChange}
                items={["customer", "partner"]}
                itemToStringLabel={item => (item === "customer" ? "Customer" : "Partner / Vendor")}
                isItemEqualToValue={(a, b) => a === b}
                searchable={false}
                showClear={false}
                className="bg-white"
              />
            </Field>
          )}
        />

        <Controller
          name="internalIdentifier"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Field.Label className="text-xs font-semibold text-slate-500">Internal ID (Optional)</Field.Label>
              <Input
                {...field}
                value={field.value || ""}
                placeholder="e.g. CUST-10024"
                className="bg-white font-mono text-xs"
              />
              {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </div>

      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <Field>
            <Field.Label className="text-xs font-semibold text-slate-500">Notes (Optional)</Field.Label>
            <Textarea
              {...field}
              value={field.value || ""}
              rows={3}
              placeholder="Add any background notes about this contact..."
              className="bg-white text-xs min-h-[80px]"
            />
          </Field>
        )}
      />

      <div className="flex justify-end pt-2">
        <Button type="submit" loading={isSubmitting}>
          {submitLabel ?? "Create Contact"}
        </Button>
      </div>
    </form>
  );
}
